import axios from 'axios'
import { i18n } from '../../plugins/i18n'
import {
  setFormValues,
  setFilterValues,
  toggleFilterValues,
} from '../../modules/mutations.js'
import { castAttributesForSnakeCase } from 'src/biz_spa/modules/string_converter'
import { _sleep } from 'src/biz_spa/modules/sleep'
import { retryPromise } from '../../modules/retryPromise'

export const companiesReviews = {
  namespaced: true,
  state: {
    data: null,
    loading: false,
    loadError: false,
    formValues: {},
    filteredCount: 0,
    requestingCount: 0,
    rejectedCount: 0,
    autoGenerateReply: '',
  },
  mutations: {
    prepareConnect(state) {
      state.loading = true
      state.loadError = false
    },
    stopConnect(state) {
      state.loading = false
    },
    readReviewsSuccess(state, payload) {
      state.data = payload
      state.requestingCount = payload.meta.approval_requesting_reviews_count
      state.rejectedCount = payload.meta.approval_rejected_reviews_count
      state.filteredCount = payload.meta.all_reviews_count
      state.loading = false
    },
    readReviewsFailure(state) {
      state.loading = false
      state.loadError = true
    },
    submitReplySuccess(state, payload) {
      state.data.data = state.data.data.map(review => {
        if (review.attributes.review_uuid === payload.reviewUuid) {
          review.attributes.reply_content = payload.updateValue
        }
        return review
      })
    },
    createCsvSuccess(state) {
      state.loading = false
    },
    createCsvFailure(state) {
      state.loading = false
      state.loadError = true
    },
    readAutoGenerateReplySuccess(state, payload) {
      state.autoGenerateReply = payload.data
    },
    setValuesForFilter(state, payload) {
      state.filteredCount = payload.meta.all_reviews_count
    },
    setFormValues,
    setFilterValues,
    toggleFilterValues,
  },
  actions: {
    readReviews({ commit }, payload) {
      commit('prepareConnect')

      return axios
        .post(`/api/companies/${ payload.company_uuid }/reviews.json${ payload.query ? '?query=' + payload.query : '' }`, payload.values)
        .then(res => {
          commit('readReviewsSuccess', res.data)
        })
        .catch(err => {
          commit('readReviewsFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    readReviewsForFilter({ commit }, payload) {
      return axios
        .post(`/api/companies/${ payload.company_uuid }/reviews.json${ payload.query ? '?query=' + payload.query : '' }`, payload.values)
        .then(res => {
          commit('setValuesForFilter', res.data)
        })
        .catch(err => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    submitReply({ commit }, { company_uuid, review_uuid, payload }) {
      return axios
        .post(`/api/companies/${company_uuid}/reviews/${review_uuid}/reply`, payload)
        .then(res => {
          payload.successFn(res)
          commit('submitReplySuccess', { updateValue: res.data.data.attributes.content, reviewUuid: review_uuid })
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesReviews.replySuccess')}, { root: true })
        })
        .catch(err => {
          payload.failureFn()
          console.error(err)
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
        })
    },
    apply({ commit }, { company_uuid, review_uuid, payload }) {
      commit('prepareConnect')
      return axios
        .post(`/api/companies/${company_uuid}/reviews/${review_uuid}/drafted_reply`, payload)
        .then(async() => {
          await _sleep(1000)
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesReviews.applySuccess') }, { root: true })
          commit('stopConnect')
          return true
        })
        .catch(() => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          commit('stopConnect')
          return false
        })
    },
    approve({ commit }, { company_uuid, review_uuid, payload }) {
      commit('prepareConnect')
      return axios
        .post(`/api/companies/${company_uuid}/reviews/${review_uuid}/drafted_reply/approval_requests/approve`, castAttributesForSnakeCase(payload))
        .then(async() => {
          await _sleep(1000)
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesReviews.approveSuccess') }, { root: true })
          commit('stopConnect')
          return true
        })
        .catch(() => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          commit('stopConnect')
          return false
        })
    },
    // 申請差し戻し
    reject({ commit }, { company_uuid, review_uuid, payload }) {
      commit('prepareConnect')
      return axios
        .post(`/api/companies/${company_uuid}/reviews/${review_uuid}/drafted_reply/approval_requests/reject`, payload)
        .then(async() => {
          await _sleep(1000)
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesReviews.rejectSuccess') }, { root: true })
          commit('stopConnect')
          return true
        })
        .catch(() => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          commit('stopConnect')
          return false
        })
    },
    // 申請取り消し
    revert({ commit }, { company_uuid, review_uuid, payload }) {
      commit('prepareConnect')
      return axios
        .delete(`/api/companies/${company_uuid}/reviews/${review_uuid}/drafted_reply`, payload)
        .then(async() => {
          await _sleep(1000)
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesReviews.revertSuccess') }, { root: true })
          commit('stopConnect')
          return true
        })
        .catch(() => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          commit('stopConnect')
          return false
        })
    },
    createCsv({ commit }, payload) {
      commit('prepareConnect')
      return axios
        .post(`/api/companies/${payload.company_uuid}/export/reviews`, payload.values)
        .then(() => {
          commit('createCsvSuccess')
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('pages.CompaniesExportResults.startDownloadPreparation')}, { root: true })
        })
        .catch(err => {
          commit('createCsvFailure')
          commit('flash/updateFlash', { type: 'alert', message: err }, { root: true })
        })
    },
    getAutoGenerateReply({ commit }, { companyUuid, reviewUuid }) {
      const retryFunc = () => {
        return axios
          .get(`/api/companies/${companyUuid}/reviews/${reviewUuid}/replies/auto_generate`)
      }
      const evalFunc = (result) => {
        const isPolling = result.data.status === 'processing'
        return !isPolling
      }
      return retryPromise(retryFunc, evalFunc, 3000)
        .then(res => {
          commit('readAutoGenerateReplySuccess', res.data)
          return true
        })
        .catch(err => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    },
    createAutoGenerateReply({ commit }, { companyUuid, reviewUuid }) {
      return axios
        .post(`/api/companies/${companyUuid}/reviews/${reviewUuid}/replies/auto_generate`)
        .then(() => {
          return true
        })
        .catch(err => {
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          return false
        })
    }
  }
}
